import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type PipelineWeeklyTrendsDataType } from '@/api/pipeline';
import Flare, {
  Area,
  Axis,
  type FlareError,
  type FlareSeriesOptions,
  Legend,
  Notes,
  Tooltip,
} from '@/components/Flare';
import { MonthDateShortFormat, MonthDateYearFormat } from '@/constants/formats';
import { getUTCTime } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  data?: PipelineWeeklyTrendsDataType[];
  isLoading?: boolean;
  error: FlareError;
};

const PipelineTrendsChart = ({ data, isLoading = false, error }: Props) => {
  const seriesData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return [
      {
        id: 'ros',
        zIndex: 2,
        name: t`Return on Spend`,
        data: data.map((d) => ({ date: d.sundayOfWeek, amount: d.returnOnSpend })),
      },
      {
        id: 'cpo',
        zIndex: 1,
        name: t`Cost per Opportunity`,
        data: data.map((d) => ({ date: d.sundayOfWeek, amount: d.costPerOpportunity })),
        yAxis: 1,
      },
    ];
  }, [data]);

  const rosFormatterShort = (value: number | string | undefined | null) =>
    numberFormat(value, { isMultiplier: true, compact: true });
  const rosFormatter = (value: number | string | undefined | null) =>
    numberFormat(value, { isMultiplier: true });
  const cpoFormatterShort = (value: number | string | undefined | null) =>
    numberFormat(value, { isCurrency: true, precision: 0, compact: true });
  const cpoFormatter = (value: number | string | undefined | null) =>
    numberFormat(value, { isCurrency: true, precision: 0 });

  return (
    <Flare
      data={seriesData}
      width="auto"
      height="auto"
      parseX={getUTCTime}
      description={t`Trends showing return on spend and cost per opportunity for your pipeline.`}
      isLoading={isLoading}
      error={error}
    >
      <Legend />
      <Area x="date" y="amount" position="overlap" />
      <Axis dualAxisId="0" position="left" labelFormat={(item) => rosFormatterShort(item.value)} />
      <Axis dualAxisId="1" position="right" labelFormat={(item) => cpoFormatterShort(item.value)} />
      <Axis
        type="datetime"
        crosshair="line"
        position="bottom"
        labelFormat={(point) => dayjs(point.value).utc().format(MonthDateShortFormat)}
      />
      <Tooltip
        shared
        titleFormat={(point) => dayjs(point?.x).utc().format(MonthDateYearFormat)}
        rowValueFormat={(item) =>
          item?.series?.name === t`Return on Spend` ? rosFormatter(item?.y) : cpoFormatter(item?.y)
        }
      />
      <Notes />
    </Flare>
  );
};

export default PipelineTrendsChart;
